// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import { PromptsList } from './PromptsList';
import { PromptEditor } from './PromptEditor';
import { useConfigMutations } from '../hooks/useConfigMutations';
import { extractPromptName, extractAnalysisType } from '../utils/prompts';
import { configApi } from '../api/config';
import type { TemplateResponse, PromptResponse, ConfigurationLevel } from '../types/config';
import type { PromptWithAnalysisType } from '../types/prompt';

interface PromptsConfigurationViewProps {
  level: ConfigurationLevel;
}

export function PromptsConfigurationView({
  level,
}: PromptsConfigurationViewProps) {
  const [selectedPrompt, setSelectedPrompt] = useState<PromptWithAnalysisType | null>(null);

  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();

  const mutations = useConfigMutations({
    level,
    orgId: level === 'workspace' ? selectedOrganizationId || undefined : undefined,
    wsId: level === 'workspace' ? selectedWorkspaceId || undefined : undefined,
  });

  // Query for all prompts (no filtering - show all types in config view)
  const { data: availablePrompts = [], isLoading: promptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', level, selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (level === 'default') {
        return configApi.getPrompts('default')
      } else {
        if (!selectedOrganizationId || !selectedWorkspaceId) {
          throw new Error('Organization and workspace must be selected for workspace prompts')
        }
        return configApi.getPrompts('workspace', selectedOrganizationId, selectedWorkspaceId)
      }
    },
    enabled: Boolean(
      level === 'default' ||
      (level === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  })

  const handlePromptFetched = (data: TemplateResponse | PromptResponse, analysisType?: string) => {
    // Store the prompt with its analysis type
    setSelectedPrompt({
      ...(data as PromptResponse),
      analysisType
    });
  };

  const handleSelectPrompt = (prompt: string) => {
    const promptName = extractPromptName(prompt);
    const analysisType = extractAnalysisType(prompt);

    mutations.getPrompt.mutate(
      { name: promptName, analysisType },
      {
        onSuccess: (data) => handlePromptFetched(data, analysisType)
      }
    );
  };

  const handlePromptChange = (prompt: PromptWithAnalysisType | null) => {
    setSelectedPrompt(prompt);
  };

  // Handle successful prompt creation
  const handlePromptCreated = (name: string) => {
    // Fetch the newly created prompt to get its full data
    handleSelectPrompt(name);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <PromptsList
        level={level}
        selectedItem={selectedPrompt}
        onSelectItem={handleSelectPrompt}
        prompts={availablePrompts}
        isLoading={promptsLoading}
      />

      <PromptEditor
        level={level}
        selectedPrompt={selectedPrompt}
        onPromptChange={handlePromptChange}
        onPromptCreated={handlePromptCreated}
      />
    </div>
  );
}
